import { Box, Group, ActionIcon, Text, Select, ColorPicker, ColorSwatch, Menu, Popover, Button } from '@mantine/core';
import {
    IconBold, IconItalic, IconStrikethrough, IconList, IconListNumbers,
    IconLink, IconUnderline, IconSuperscript, IconSubscript,
    IconAlignLeft, IconAlignCenter, IconAlignRight, IconAlignJustified,
    IconPhoto, IconTable, IconPalette, IconRotateClockwise,
    IconCode, IconH1, IconH2, IconQuote, IconPilcrow, IconTextSize, IconMessageCircle
} from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useEditorState, type Editor } from '@tiptap/react';
import type { RichTextPresetStyle } from '@/services/rich-text-presets.api';
import { ACCEPTED_IMAGE_MIME_TYPES } from '@/services/assets.api';
import { useImageUpload } from '@/features/editor/hooks/useImageUpload';
import { CalloutPreview } from './CalloutPreview';
import {
    FONT_FAMILIES,
    FONT_SIZES,
    applyTextStyle,
    matchFontFamily,
    matchFontSize,
    withCurrentValue,
    currentTextStyle,
} from './fontControls';
import { collectUsedColors, type UsedColors } from './flowColors';
import { insertUploadedImages } from './insertUploadedImages';

type Preset = { id: string; name: string; style: RichTextPresetStyle };

interface EditorToolbarProps {
    editor: Editor;
    textColor: string;
    bgColor: string;
    setTextColor: (value: string) => void;
    setBgColor: (value: string) => void;
    textPresets: Preset[];
    calloutPresets: Preset[];
    onApplyPreset: (style: RichTextPresetStyle) => void;
    flowContent?: string[];
}

const Divider = () => (
    <div style={{ width: 1, height: 20, backgroundColor: '#dee2e6', margin: '0 4px' }} />
);

/** Colors already used in the flow, clicked to apply. Hidden when there are none. */
const UsedColorSwatches = ({ colors, onPick }: { colors: string[]; onPick: (color: string) => void }) =>
    colors.length === 0 ? null : (
        <Box mt={8} maw={200}>
            <Text size="xs" c="dimmed" mb={4}>Used in this flow</Text>
            <Group gap={6}>
                {colors.map((color) => (
                    <ColorSwatch
                        key={color}
                        component="button"
                        type="button"
                        color={color}
                        size={20}
                        radius="sm"
                        aria-label={`Use ${color}`}
                        title={color}
                        onClick={() => onPick(color)}
                        style={{ cursor: 'pointer' }}
                    />
                ))}
            </Group>
        </Box>
    );

/** Swatch under a color button showing the color applied at the selection. */
const ColorBar = ({ color }: { color: string }) => (
    <Box
        data-testid="color-bar"
        style={{ position: 'absolute', bottom: -2, left: 0, right: 0, height: 3, backgroundColor: color }}
    />
);

export function EditorToolbar({
    editor,
    textColor,
    bgColor,
    setTextColor,
    setBgColor,
    textPresets,
    calloutPresets,
    onApplyPreset,
    flowContent = [],
}: EditorToolbarProps) {
    // Read the font in force at the selection straight off the document. Derived
    // here rather than in the parent because the parent renders once before its
    // editor exists, and the subscription would cache that empty first reading.
    // Colors are read the same way, so the color buttons show what the selected
    // text actually carries rather than whichever color was last picked.
    const { fontFamily, fontSize, highlightColor, appliedTextColor } = useEditorState({
        editor,
        selector: ({ editor }) => ({
            ...currentTextStyle(editor),
            // A highlight with no color attribute renders in the extension's default yellow.
            highlightColor: editor.isActive('highlight')
                ? ((editor.getAttributes('highlight').color as string | undefined) ?? '#FFFF00')
                : null,
            appliedTextColor: (editor.getAttributes('textStyle').color as string | undefined) ?? null,
        }),
    });

    // The document's own spelling ('arial', 'Arial, sans-serif') mapped onto the
    // dropdown entries, so a font the text really uses is never shown as blank.
    const selectedFontFamily = matchFontFamily(fontFamily);
    const selectedFontSize = matchFontSize(fontSize);

    // Worked out when a color popover opens rather than on every keystroke. A
    // color picked while it is open shows up the next time it opens, so dragging
    // across the picker does not flood the row with intermediate shades.
    const [usedColors, setUsedColors] = useState<UsedColors>({ text: [], highlight: [] });
    const refreshUsedColors = () => setUsedColors(collectUsedColors([...flowContent, editor.getHTML()]));

    const applyHighlight = (color: string) => {
        setBgColor(color);
        editor.chain().focus().setHighlight({ color }).run();
    };
    const applyTextColor = (color: string) => {
        setTextColor(color);
        editor.chain().focus().setColor(color).run();
    };

    const fileInputRef = useRef<HTMLInputElement>(null);
    // Shared with the editor's drag-and-drop and paste paths so all three
    // accept the same files and report failures the same way.
    const { uploading, uploadFlowContent } = useImageUpload();

    const handleImageSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        // Reset immediately so picking the same file twice still fires onChange.
        event.target.value = '';
        if (files.length === 0) return;

        const assets = await uploadFlowContent(files);
        // The editor may have been destroyed while the upload ran.
        insertUploadedImages(editor, assets);
    };

    return (
        <>
            {/* Header with text type */}
            <Box px="sm" py={6} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                <Group gap="xs" align="center">
                    <IconCode size={16} style={{ color: '#495057' }} />
                    <Text size="sm" fw={500} c="dimmed">PARAGRAPH</Text>
                </Group>
            </Box>

            {/* Toolbar - First Row */}
            <Box px="sm" py={6} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#ffffff' }}>
                <Group gap={4} wrap="nowrap">
                    {/* Basic formatting */}
                    <ActionIcon
                        variant={editor.isActive('bold') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconBold size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('italic') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconItalic size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('underline') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconUnderline size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('strike') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconStrikethrough size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('superscript') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleSuperscript().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconSuperscript size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('subscript') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleSubscript().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconSubscript size={16} />
                    </ActionIcon>

                    <Divider />

                    {/* Font size selector */}
                    <Select
                        size="xs"
                        placeholder="14"
                        value={selectedFontSize}
                        data={withCurrentValue(selectedFontSize, FONT_SIZES)}
                        onChange={(value) => {
                            if (value) applyTextStyle(editor, { fontSize: `${value}px` });
                        }}
                        w={60}
                        styles={{ input: { fontSize: '12px' } }}
                    />

                    {/* Font family selector */}
                    <Select
                        size="xs"
                        placeholder="Helvetica"
                        value={selectedFontFamily}
                        data={withCurrentValue(selectedFontFamily, FONT_FAMILIES)}
                        onChange={(value) => {
                            if (value) applyTextStyle(editor, { fontFamily: value });
                        }}
                        w={100}
                        styles={{ input: { fontSize: '12px' } }}
                    />

                    <Menu position="bottom-start">
                        <Menu.Target>
                            <ActionIcon size="sm" variant="subtle" color="gray" aria-label="Text styles">
                                <IconTextSize size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>Block Style</Menu.Label>
                            <Menu.Item leftSection={<IconPilcrow size={16} />} onClick={() => editor.chain().focus().setParagraph().run()}>
                                Paragraph
                            </Menu.Item>
                            <Menu.Item leftSection={<IconH1 size={16} />} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                                Heading 1
                            </Menu.Item>
                            <Menu.Item leftSection={<IconH2 size={16} />} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                                Heading 2
                            </Menu.Item>
                            <Menu.Item leftSection={<IconQuote size={16} />} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                                Quote
                            </Menu.Item>
                            {textPresets.length > 0 && (
                                <>
                                    <Menu.Label>Text Styles</Menu.Label>
                                    {textPresets.map((preset) => (
                                        <Menu.Item key={preset.id} leftSection={<IconTextSize size={16} />} onClick={() => onApplyPreset(preset.style)}>
                                            {preset.name}
                                        </Menu.Item>
                                    ))}
                                </>
                            )}
                        </Menu.Dropdown>
                    </Menu>

                    <Menu position="bottom-start" width={282}>
                        <Menu.Target>
                            <ActionIcon size="sm" variant="subtle" color="gray" aria-label="Callout boxes">
                                <IconMessageCircle size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>Callout Boxes</Menu.Label>
                            {calloutPresets.length > 0 ? (
                                calloutPresets.map((preset) => (
                                    <Menu.Item key={preset.id} p={6} onClick={() => onApplyPreset(preset.style)}>
                                        <CalloutPreview preset={preset} />
                                    </Menu.Item>
                                ))
                            ) : (
                                <Menu.Item disabled>No callout presets</Menu.Item>
                            )}
                        </Menu.Dropdown>
                    </Menu>

                    <Divider />

                    {/* Color controls */}
                    <Popover position="bottom-start" shadow="md" withinPortal onOpen={refreshUsedColors}>
                        <Popover.Target>
                            <ActionIcon
                                size="sm"
                                variant={highlightColor ? 'light' : 'subtle'}
                                color="gray"
                                aria-label="Highlight color"
                                aria-pressed={!!highlightColor}
                            >
                                <Box style={{ position: 'relative', display: 'flex' }}>
                                    <IconPalette size={16} />
                                    {highlightColor && <ColorBar color={highlightColor} />}
                                </Box>
                            </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <Box p="xs">
                                <Text size="xs" mb={4}>Highlight Color</Text>
                                <ColorPicker
                                    format="hex"
                                    value={highlightColor ?? bgColor}
                                    onChange={applyHighlight}
                                />
                                <UsedColorSwatches colors={usedColors.highlight} onPick={applyHighlight} />
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    fullWidth
                                    mt={6}
                                    onClick={() => editor.chain().focus().unsetHighlight().run()}
                                >
                                    Remove highlight
                                </Button>
                            </Box>
                        </Popover.Dropdown>
                    </Popover>

                    <Popover position="bottom-start" shadow="md" withinPortal onOpen={refreshUsedColors}>
                        <Popover.Target>
                            <ActionIcon
                                size="sm"
                                variant={appliedTextColor ? 'light' : 'subtle'}
                                color="gray"
                                aria-label="Text color"
                                aria-pressed={!!appliedTextColor}
                            >
                                {/* Sized like the 16px icons so its bar spans the same width. */}
                                <Box style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16 }}>
                                    <Text fz={15} fw={700} lh={1}>A</Text>
                                    {appliedTextColor && <ColorBar color={appliedTextColor} />}
                                </Box>
                            </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <Box p="xs">
                                <Text size="xs" mb={4}>Text Color</Text>
                                <ColorPicker
                                    format="hex"
                                    value={appliedTextColor ?? textColor}
                                    onChange={applyTextColor}
                                />
                                <UsedColorSwatches colors={usedColors.text} onPick={applyTextColor} />
                                {/* Removes the color mark so the text falls back to the
                                    page's default color rather than pinning it to black. */}
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    fullWidth
                                    mt={6}
                                    onClick={() => {
                                        setTextColor('#000000');
                                        editor.chain().focus().unsetColor().run();
                                    }}
                                >
                                    Reset to default
                                </Button>
                            </Box>
                        </Popover.Dropdown>
                    </Popover>
                </Group>
            </Box>

            {/* Toolbar - Second Row */}
            <Box px="sm" py={6} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#ffffff' }}>
                <Group gap={4} wrap="nowrap">
                    {/* Alignment */}
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'left' }) ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        size="sm"
                        color="gray"
                    >
                        <IconAlignLeft size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'center' }) ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        size="sm"
                        color="gray"
                    >
                        <IconAlignCenter size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'right' }) ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        size="sm"
                        color="gray"
                    >
                        <IconAlignRight size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'justify' }) ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                        size="sm"
                        color="gray"
                    >
                        <IconAlignJustified size={16} />
                    </ActionIcon>

                    <Divider />

                    {/* Lists */}
                    <ActionIcon
                        variant={editor.isActive('bulletList') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconList size={16} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('orderedList') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconListNumbers size={16} />
                    </ActionIcon>

                    <Divider />

                    {/* Table */}
                    <Menu position="bottom-start">
                        <Menu.Target>
                            <ActionIcon size="sm" variant="subtle" color="gray">
                                <IconTable size={16} />
                            </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
                                Insert 3×3 Table
                            </Menu.Item>
                            <Menu.Item onClick={() => editor.chain().focus().insertTable({ rows: 5, cols: 5, withHeaderRow: true }).run()}>
                                Insert 5×5 Table
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Group>
            </Box>

            {/* Toolbar - Third Row */}
            <Box px="sm" py={6} style={{ borderBottom: '1px solid #dee2e6', backgroundColor: '#ffffff' }}>
                <Group gap={4} wrap="nowrap">
                    {/* Link */}
                    <ActionIcon
                        variant={editor.isActive('link') ? 'filled' : 'subtle'}
                        onClick={() => {
                            const previousUrl = editor.getAttributes('link').href;
                            const url = window.prompt('URL', previousUrl);
                            if (url === null) return;
                            if (url === '') {
                                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                                return;
                            }
                            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
                        }}
                        size="sm"
                        color="gray"
                    >
                        <IconLink size={16} />
                    </ActionIcon>

                    {/* Image */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_IMAGE_MIME_TYPES.join(',')}
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleImageSelected}
                    />
                    <ActionIcon
                        variant="subtle"
                        onClick={() => fileInputRef.current?.click()}
                        loading={uploading}
                        size="sm"
                        color="gray"
                        aria-label="Add image"
                    >
                        <IconPhoto size={16} />
                    </ActionIcon>

                    {/* Code */}
                    <ActionIcon
                        variant={editor.isActive('codeBlock') ? 'filled' : 'subtle'}
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        size="sm"
                        color="gray"
                    >
                        <IconCode size={16} />
                    </ActionIcon>

                    <Divider />

                    {/* Undo/Redo */}
                    <ActionIcon
                        variant="subtle"
                        onClick={() => editor.chain().focus().undo().run()}
                        size="sm"
                        color="gray"
                        disabled={!editor.can().undo()}
                    >
                        <IconRotateClockwise size={16} style={{ transform: 'scaleX(-1)' }} />
                    </ActionIcon>
                    <ActionIcon
                        variant="subtle"
                        onClick={() => editor.chain().focus().redo().run()}
                        size="sm"
                        color="gray"
                        disabled={!editor.can().redo()}
                    >
                        <IconRotateClockwise size={16} />
                    </ActionIcon>
                </Group>
            </Box>
        </>
    );
}
